import type {
  Section,
  Step,
  Field as FieldFormality,
} from "@/types/entities/formality-request";
import type { Step as FormStep } from "@sdt/render-form-engine";

export function getAllFields(steps: readonly Step[]): FieldFormality[] {
  const out: FieldFormality[] = [];
  for (const step of steps) {
    const sections: readonly Section[] = step.sections ?? [];
    for (const section of sections) {
      const fields: FieldFormality[] = section.fields ?? [];
      out.push(...fields);
    }
  }
  return out;
}

export function resolveFilledFields(
  field: FormStep["sections"][number]["fields"][number],
  filledFieldsFromFormData: Map<string, FieldFormality["filled_fields"]>,
) {
  const fromMap = filledFieldsFromFormData.get(field.name);
  const currentFilled =
    (field.filled_fields as unknown as NonNullable<
      FieldFormality["filled_fields"]
    > | null) ?? [];
  const existingFilled =
    currentFilled.length > 0 ? currentFilled : (fromMap ?? []);
  const raw =
    field.type === "file" ? (fromMap ?? currentFilled) : existingFilled;

  const sorted = (raw ?? [])
    .filter((ff) => ff.value !== null && ff.value !== undefined)
    .sort(
      (a, b) =>
        new Date(a.updated_at ?? 0).getTime() -
        new Date(b.updated_at ?? 0).getTime(),
    );

  const lastWithCorrection = [...sorted].reverse().find((ff) => ff.correction);
  const best = [...sorted]
    .reverse()
    .find(
      (ff) => ff.value !== null && ff.value !== undefined && ff.value !== "",
    );

  const selected: FieldFormality["filled_fields"] = [];
  if (lastWithCorrection && lastWithCorrection.id !== best?.id) {
    selected.push(lastWithCorrection);
  }
  if (best) {
    selected.push(best);
  } else if (sorted.length > 0) {
    selected.push(sorted[sorted.length - 1]);
  }

  return selected;
}

export function prepopulateSection(
  section: FormStep["sections"][number],
  filledFieldsFromFormData: Map<string, FieldFormality["filled_fields"]>,
) {
  return {
    ...section,
    fields: (section.fields ?? []).map((field) => ({
      ...field,
      filled_fields: resolveFilledFields(
        field,
        filledFieldsFromFormData,
      ) as unknown as typeof field.filled_fields,
    })),
  };
}

export function prepopulateSteps(
  steps: FormStep[],
  filledFieldsFromFormData: Map<string, FieldFormality["filled_fields"]>,
): FormStep[] {
  return steps.map((step) => ({
    ...step,
    sections: (step.sections ?? []).map((section) =>
      prepopulateSection(section, filledFieldsFromFormData),
    ),
  }));
}

// Save files
export const extractFileFields = (data: Record<string, unknown>) => {
  const result: Record<string, File[]> = {};

  Object.entries(data).forEach(([key, value]) => {
    if (value instanceof File && !("id" in value)) {
      result[key] = [value];
      return;
    }
    if (Array.isArray(value)) {
      const files = value.filter(
        (v): v is File => v instanceof File && !("id" in v),
      );
      if (files.length > 0) {
        result[key] = files;
      }
    }
  });

  return result;
};

export const removeFileFields = (
  data: Record<string, unknown>,
  fileFieldNames?: Set<string>,
) => {
  const result: Record<string, unknown> = {};

  Object.entries(data).forEach(([key, value]) => {
    if (fileFieldNames?.has(key)) return;

    if (value instanceof File) return;

    if (Array.isArray(value)) {
      if (value.some((v) => v instanceof File)) return;
      result[key] = value;
      return;
    }

    if (value && typeof value === "object") {
      result[key] = removeFileFields(value as Record<string, unknown>);
      return;
    }

    result[key] = value;
  });

  return result;
};
